/**
 * Hooks para CON-01 (Plan de Cuentas), CON-02 (Centros de Costo),
 * CON-03 (Comprobantes), CON-04 (Reportes), CON-05 (Análisis),
 * CON-06 (Conciliación Bancaria), CON-07 (Rechazo de Borradores)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

/** Importar un archivo puede tardar minutos: el timeout por defecto (30 s) corta
 *  el request mientras el servidor sigue escribiendo. */
const IMPORT_TIMEOUT_MS = 300_000

// ─── Types ────────────────────────────────────────────────────────────────────

export type AccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense' | 'other'

export interface ChartOfAccount {
  id: string
  code: string
  name: string
  description: string
  account_type: AccountType
  sii_code: string
  sii_account_name: string | null
  parent: string | null
  parent_code: string | null
  parent_name: string | null
  is_imputable: boolean
  is_active: boolean
  account_currency: string  // '' = base currency (CLP)
  cash_flow_category: CashFlowCategory
  expense_category: ExpenseCategory
  balance_section: BalanceSection
  has_movements: boolean
  created_at: string
  updated_at: string
}

export type CashFlowCategory = 'efectivo' | 'operacion' | 'inversion' | 'financiamiento' | 'sin_clasificar'

export const CASH_FLOW_CATEGORY_LABELS: Record<CashFlowCategory, string> = {
  efectivo: 'Efectivo y equivalentes',
  operacion: 'Actividades de operación',
  inversion: 'Actividades de inversión',
  financiamiento: 'Actividades de financiamiento',
  sin_clasificar: 'Sin clasificar',
}

/**
 * Seccion del Estado de Resultados donde cae una cuenta de GASTO.
 * '' = se deduce del codigo, que solo funciona con la numeracion chilena
 * (41 costo, 42/43 administracion, 44/45 financieros).
 */
export type ExpenseCategory = '' | 'cost' | 'admin' | 'financial' | 'other'

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  '': 'Automática (por el código de la cuenta)',
  cost: 'Costo de ventas',
  admin: 'Gastos de administración y ventas',
  financial: 'Gastos financieros',
  other: 'Otros gastos',
}

/**
 * Corriente o no corriente en el Balance Clasificado.
 *
 * '' = se deduce del codigo, que solo funciona con la numeracion chilena (11
 * activo corriente, 21 pasivo corriente). Es el gemelo de ExpenseCategory: con el
 * PCGE peruano no coincide ningun prefijo —el efectivo es la cuenta 10 y el pasivo
 * el elemento 4— y el balance entero sale como no corriente.
 */
export type BalanceSection = '' | 'current' | 'non_current'

export const BALANCE_SECTION_LABELS: Record<BalanceSection, string> = {
  '': 'Automática (por el código de la cuenta)',
  current: 'Corriente',
  non_current: 'No corriente',
}

export interface AccountNameHistoryEntry {
  id: string
  old_name: string
  new_name: string
  changed_by: string | null
  changed_at: string
}

export interface AccountNameHistoryResponse {
  account_code: string
  current_name: string
  history: AccountNameHistoryEntry[]
  count: number
}

export interface SIIAccount {
  id: number
  code: string
  name: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface SIIAccountListResponse {
  count: number
  accounts: SIIAccount[]
}

export interface AccountListResponse {
  count: number
  accounts: ChartOfAccount[]
}

export interface CostCenter {
  id: string
  code: string
  name: string
  parent: string | null
  parent_code: string | null
  parent_name: string | null
  /** Centro de responsabilidad: qué controla el responsable y qué se le puede medir. */
  center_type: 'cost' | 'revenue' | 'profit'
  center_type_label: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CostCenterListResponse {
  count: number
  cost_centers: CostCenter[]
}

export type VoucherType = 'manual' | 'sale' | 'purchase' | 'adjustment' | 'reversal'
export type VoucherStatus = 'draft' | 'approved' | 'rejected'

export interface VoucherLine {
  id?: string
  account: string
  account_code?: string
  account_name?: string
  cost_center?: string | null
  cost_center_code?: string | null
  cost_center_name?: string | null
  glosa: string
  debe: string
  haber: string
  order?: number
  // Multi-currency (read-only, populated by server)
  currency?: string
  debe_original?: string | null
  haber_original?: string | null
  exchange_rate?: string | null
  exchange_rate_date?: string | null
}

export interface AccountingVoucherListItem {
  id: string
  number: string
  voucher_type: VoucherType
  date: string
  description: string
  status: VoucherStatus
  source_type: string
  source_id: string | null
  currency: string
  total_debe: string
  total_haber: string
  line_count: number
  created_at: string
}

export interface AccountingVoucher {
  id: string
  number: string
  voucher_type: VoucherType
  date: string
  description: string
  status: VoucherStatus
  notes: string
  source_type: string
  source_id: string | null
  currency: string           // '' = CLP (base currency)
  exchange_rate: string | null
  exchange_rate_date: string | null
  lines: VoucherLine[]
  total_debe: string
  total_haber: string
  is_balanced: boolean
  reverses: string | null
  reverses_number: string | null
  created_at: string
  updated_at: string
}

// CON-05: Análisis de Cuentas
export interface AnalysisMovement {
  date: string
  voucher_id?: string
  voucher_number: string
  voucher_description: string
  glosa: string
  cost_center_code: string | null
  cost_center_name: string | null
  debe: string
  haber: string
  running_balance: string
}

export interface AccountAnalysisResponse {
  account_code: string
  account_name: string
  date_from: string
  date_to: string
  opening_balance: string
  count: number
  movements: AnalysisMovement[]
}

// CON-06: Conciliación Bancaria
export type ReconciliationStatus = 'in_progress' | 'closed'

export interface BankReconciliation {
  id: string
  bank_account: string
  bank_account_code: string | null
  bank_account_name: string | null
  bank_account_currency: string  // '' = CLP, 'USD' = USD account
  period_start: string
  period_end: string
  status: ReconciliationStatus
  difference: string
  statement_id: string | null
  closed_at: string | null
  created_at: string
}

export interface BankStatementLine {
  id: string
  transaction_date: string
  description: string
  reference: string
  amount: string
  status: 'pending' | 'reconciled_auto' | 'reconciled_manual'
  voucher_line: string | null
}

export interface SystemLine {
  id: string
  date: string
  voucher_number: string
  glosa: string
  debe: string
  haber: string
  net: string
  matched: boolean
}

export interface BankReconciliationDetail extends BankReconciliation {
  system_lines: SystemLine[]
  statement_lines: BankStatementLine[]
}

export interface VoucherListResponse {
  count: number
  pages: number
  vouchers: AccountingVoucherListItem[]
}

// Reports
export interface JournalLine {
  account_code: string
  account_name: string
  cost_center_code: string | null
  glosa: string
  debe: string
  haber: string
}

export interface JournalEntry {
  id: string
  number: string
  date: string
  description: string
  voucher_type: VoucherType
  /** Cierre o apertura (regla única de los reportes, backend `exclusiones.py`).
   *  El diario los muestra todos; el rótulo dice cuáles no suman en otros reportes. */
  closing_kind?: 'closing' | 'reopening' | 'genesis' | null
  total_debe: string
  total_haber: string
  lines: JournalLine[]
}

export interface JournalResponse {
  date_from: string
  date_to: string
  count: number
  entries: JournalEntry[]
}

export interface LedgerMovement {
  date: string
  voucher_id?: string
  voucher_number: string
  voucher_description: string
  glosa: string
  debe: string
  haber: string
  balance: string
}

export interface LedgerAccountData {
  account_code: string
  account_name: string
  total_debe: string
  total_haber: string
  /** Saldo de la cuenta antes de `date_from`; el saldo corrido parte de acá. */
  opening_balance: string
  closing_balance: string
  count: number
  movements: LedgerMovement[]
}

export interface LedgerResponse extends LedgerAccountData {
  date_from: string
  date_to: string
}

export interface LedgerAllResponse {
  date_from: string
  date_to: string
  account_count: number
  accounts: LedgerAccountData[]
}

export interface TrialBalanceRow {
  account_code: string
  account_name: string
  account_type: AccountType
  opening_debe: string
  opening_haber: string
  period_debe: string
  period_haber: string
  final_debe: string
  final_haber: string
}

export interface TrialBalanceResponse {
  date_from: string
  date_to: string
  count: number
  accounts: TrialBalanceRow[]
}

// Balance 8 Columnas
export interface EightColumnBalanceRow {
  account_code: string
  account_name: string
  account_type: string
  debitos: string
  creditos: string
  deudor: string
  acreedor: string
  activo: string
  pasivo: string
  perdida: string
  ganancia: string
}

export interface EightColumnBalanceResponse {
  date_from: string
  date_to: string
  count: number
  accounts: EightColumnBalanceRow[]
  totals: Record<string, string>
  resultado_ejercicio: string
  generated_at: string
  currency: string
}

// Balance Clasificado
export interface ClassifiedBalanceAccount {
  account_code: string
  account_name: string
  balance: string
}

export interface ClassifiedBalanceSection {
  accounts: ClassifiedBalanceAccount[]
  total: string
}

export interface ClassifiedBalanceEquity {
  accounts: ClassifiedBalanceAccount[]
  result_current_year: string
  total: string
}

export interface ClassifiedBalanceResponse {
  date_to: string
  company: {
    legal_name: string
    tax_id: string
    giro: string
    address: string
  }
  current_assets: ClassifiedBalanceSection
  non_current_assets: ClassifiedBalanceSection
  total_assets: string
  current_liabilities: ClassifiedBalanceSection
  non_current_liabilities: ClassifiedBalanceSection
  equity: ClassifiedBalanceEquity
  total_liabilities_equity: string
  currency: string
}

// Estado de Resultados
export interface IncomeStatementLineItem {
  label: string
  amount: string
  is_subtotal: boolean
  indent: number
  account_code?: string
}

export interface IncomeStatementResponse {
  date_from: string
  date_to: string
  company: {
    legal_name: string
    tax_id: string
    giro: string
    address: string
  }
  line_items: IncomeStatementLineItem[]
  net_result: string
  currency: string
}

// ─── Estado de Flujo de Efectivo (NIC 7) ──────────────────────────────────────

export interface CashFlowActivity {
  label: string
  total: string
  lineas: { account_code: string; label: string; amount: string }[]
}

export interface CashFlowStatementResponse {
  date_from: string
  date_to: string
  efectivo_inicial: string
  efectivo_final: string
  variacion_efectivo: string
  actividades: {
    operacion: CashFlowActivity
    inversion: CashFlowActivity
    financiamiento: CashFlowActivity
    sin_clasificar: CashFlowActivity
  }
  transferencias_internas: string
  cuadre: {
    suma_flujos: string
    variacion_saldos: string
    diferencia: string
    cuadra: boolean
  }
  warnings: string[]
  company: {
    legal_name: string
    tax_id: string
    business_activity?: string
    address: string
  }
  currency: string
}

// ─── Cruce Contable (Audit) ───────────────────────────────────────────────────

export interface AuditMissingDoc {
  id: string
  type: string
  label: string
  folio: string
  date: string
  total: string
  status: string
}

export interface AuditDraftVoucher {
  id: string
  number: string
  voucher_type: VoucherType
  source_type: string
  date: string
  description: string
  total_debe: string
  total_haber: string
  is_balanced: boolean
}

export interface AccountingAuditResponse {
  kpis: {
    total_missing_vouchers: number
    total_draft_vouchers: number
    missing_by_type: {
      sale_documents: number
      customer_payments: number
      supplier_invoices: number
      supplier_payments: number
      factoring_operations: number
    }
  }
  missing: {
    sale_documents: AuditMissingDoc[]
    customer_payments: AuditMissingDoc[]
    supplier_invoices: AuditMissingDoc[]
    supplier_payments: AuditMissingDoc[]
    factoring_operations: AuditMissingDoc[]
  }
  draft_vouchers: AuditDraftVoucher[]
  generated_at: string
}

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const accountingKeys = {
  accounts: (params?: object) => ['accounting', 'accounts', params] as const,
  account: (id: string) => ['accounting', 'accounts', id] as const,
  costCenters: (params?: object) => ['accounting', 'cost-centers', params] as const,
  costCenter: (id: string) => ['accounting', 'cost-centers', id] as const,
  vouchers: (params?: object) => ['accounting', 'vouchers', params] as const,
  voucher: (id: string) => ['accounting', 'vouchers', id] as const,
  journal: (params?: object) => ['accounting', 'reports', 'journal', params] as const,
  ledger: (params?: object) => ['accounting', 'reports', 'ledger', params] as const,
  trialBalance: (params?: object) => ['accounting', 'reports', 'trial-balance', params] as const,
  eightColumnBalance: (params?: object) => ['accounting', 'reports', 'eight-column-balance', params] as const,
  analysis: (params?: object) => ['accounting', 'analysis', params] as const,
  reconciliations: (params?: object) => ['accounting', 'bank-reconciliation', params] as const,
  reconciliation: (id: string) => ['accounting', 'bank-reconciliation', id] as const,
  classifiedBalance: (params?: object) => ['accounting', 'reports', 'classified-balance', params] as const,
  incomeStatement: (params?: object) => ['accounting', 'reports', 'income-statement', params] as const,
  cashFlow: (params?: object) => ['accounting', 'reports', 'cash-flow', params] as const,
  audit: (params?: object) => ['accounting', 'audit', params] as const,
  attachments: (voucherId: string) => ['accounting', 'vouchers', voucherId, 'attachments'] as const,
}

// ─── CON-01: Plan de Cuentas ──────────────────────────────────────────────────

export function useAccounts(params?: {
  account_type?: AccountType
  is_active?: string
  is_imputable?: string
}) {
  return useQuery({
    queryKey: accountingKeys.accounts(params),
    queryFn: async () => {
      const res = await apiClient.get<AccountListResponse>('/accounting/accounts/', { params })
      return res.data
    },
  })
}

export function useCreateAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<ChartOfAccount>) =>
      apiClient.post<ChartOfAccount>('/accounting/accounts/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'accounts'] }),
  })
}

export function useUpdateAccount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<ChartOfAccount>) =>
      apiClient.patch<ChartOfAccount>(`/accounting/accounts/${id}/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'accounts'] }),
  })
}

export function useDeleteAccount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (opts?: { permanent?: boolean }) =>
      apiClient.delete(`/accounting/accounts/${id}/${opts?.permanent ? '?permanent=true' : ''}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'accounts'] }),
  })
}

export function useRestoreAccount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient.post(`/accounting/accounts/${id}/restore/`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'accounts'] }),
  })
}

export function useAccountNameHistory(id: string, enabled = true) {
  return useQuery({
    queryKey: ['accounting', 'accounts', id, 'name-history'],
    queryFn: () =>
      apiClient.get<AccountNameHistoryResponse>(`/accounting/accounts/${id}/name-history/`).then(r => r.data),
    enabled: enabled && !!id,
  })
}

// ─── CON-01: Importar Plan de Cuentas desde CSV ──────────────────────────────

export interface CSVImportResult {
  created: number
  updated: number
  errors: { row: number; error: string | Record<string, string[]> }[]
  total_rows: number
}

export function useImportAccountsCSV() {
  const qc = useQueryClient()
  return useMutation<CSVImportResult, Error, { file: File; mapping: Record<string, string> }>({
    mutationFn: async ({ file, mapping }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('mapping', JSON.stringify(mapping))
      const res = await apiClient.post('/accounting/accounts/import-csv/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // ⚠️ El default de 30 s es correcto para un listado y absurdo para importar
        // cientos de filas: una importación real de 745 clientes tardó 35 s y el
        // navegador mostró «timeout» sobre un archivo que SÍ se había importado
        // completo — el peor error posible, porque invita a reintentar.
        timeout: IMPORT_TIMEOUT_MS,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting', 'accounts'] })
    },
  })
}

// ─── CON-02: Centros de Costo ─────────────────────────────────────────────────

/**
 * Centros de costo activos.
 *
 * El endpoint exige el módulo `accounting.cost_centers`, pero se consume también
 * desde ventas, compras y nómina (`CostCenterAllocator`, `EmployeeModal`), donde
 * el usuario puede no tenerlo. En ese caso responde **403** y quien lo llame
 * tiene que distinguirlo de "no hay centros de costo": ver `isForbidden`.
 *
 * No se reintenta un 401/403 — un permiso no aparece por insistir, y en
 * producción se vieron dos llamadas por segundo contra el mismo 403.
 */
export function useCostCenters(params?: { is_active?: string }) {
  const query = useQuery({
    queryKey: accountingKeys.costCenters(params),
    queryFn: async () => {
      const res = await apiClient.get<CostCenterListResponse>('/accounting/cost-centers/', { params })
      return res.data
    },
    retry: (failureCount, error: any) => {
      const status = error?.response?.status
      if (status === 401 || status === 403) return false
      return failureCount < 2
    },
  })
  const status = (query.error as any)?.response?.status
  return { ...query, isForbidden: status === 401 || status === 403 }
}

export function useCreateCostCenter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<CostCenter>) =>
      apiClient.post<CostCenter>('/accounting/cost-centers/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'cost-centers'] }),
  })
}

export function useUpdateCostCenter(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<CostCenter>) =>
      apiClient.patch<CostCenter>(`/accounting/cost-centers/${id}/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'cost-centers'] }),
  })
}

// ─── CON-03: Comprobantes ─────────────────────────────────────────────────────

export function useVouchers(params?: {
  status?: VoucherStatus
  voucher_type?: VoucherType
  search?: string
  date_from?: string
  date_to?: string
  has_source?: 'true'
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: accountingKeys.vouchers(params),
    queryFn: async () => {
      const res = await apiClient.get<VoucherListResponse>('/accounting/vouchers/', { params })
      return res.data
    },
  })
}

export function useVoucher(id: string) {
  return useQuery({
    queryKey: accountingKeys.voucher(id),
    queryFn: async () => {
      const res = await apiClient.get<AccountingVoucher>(`/accounting/vouchers/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateVoucher() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<AccountingVoucher>) =>
      apiClient.post<AccountingVoucher>('/accounting/vouchers/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] }),
  })
}

export function useUpdateVoucher(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<AccountingVoucher>) =>
      apiClient.patch<AccountingVoucher>(`/accounting/vouchers/${id}/`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] })
    },
  })
}

export function useApproveVoucher() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<AccountingVoucher>(`/accounting/vouchers/${id}/approve/`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] }),
  })
}

export function useReverseVoucher() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, date, description }: { id: string; date?: string; description?: string }) =>
      apiClient.post<AccountingVoucher>(`/accounting/vouchers/${id}/reverse/`, { date, description }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] }),
  })
}

export function useDeleteVoucher() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/accounting/vouchers/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] }),
  })
}

export interface BulkVoucherResult {
  approved?: { id: string; number: string }[]
  rejected?: string[]
  skipped: { id: string; reason: string }[]
  approved_count?: number
  rejected_count?: number
  skipped_count: number
}

export function useBulkApproveVouchers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (voucherIds: string[]) =>
      apiClient.post<BulkVoucherResult>('/accounting/vouchers/bulk-approve/', { voucher_ids: voucherIds }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] }),
  })
}

export function useBulkRejectVouchers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ voucherIds, reason }: { voucherIds: string[]; reason?: string }) =>
      apiClient.post<BulkVoucherResult>('/accounting/vouchers/bulk-reject/', { voucher_ids: voucherIds, reason }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] }),
  })
}

// ─── CON-04: Reportes ─────────────────────────────────────────────────────────

export function useJournalReport(params: { date_from: string; date_to: string } | null) {
  return useQuery({
    queryKey: accountingKeys.journal(params ?? undefined),
    queryFn: async () => {
      const res = await apiClient.get<JournalResponse>('/accounting/reports/journal/', { params: params! })
      return res.data
    },
    enabled: !!(params?.date_from && params?.date_to),
  })
}

export function useLedgerReport(
  params: { date_from: string; date_to: string; account_id?: string } | null
) {
  return useQuery({
    queryKey: accountingKeys.ledger(params ?? undefined),
    queryFn: async () => {
      if (params?.account_id) {
        const res = await apiClient.get<LedgerResponse>('/accounting/reports/ledger/', { params: params! })
        return res.data
      }
      const res = await apiClient.get<LedgerAllResponse>('/accounting/reports/ledger/', { params: params! })
      return res.data
    },
    enabled: !!(params?.date_from && params?.date_to),
  })
}

export function useTrialBalanceReport(
  params: { date_from: string; date_to: string } | null
) {
  return useQuery({
    queryKey: accountingKeys.trialBalance(params ?? undefined),
    queryFn: async () => {
      const res = await apiClient.get<TrialBalanceResponse>('/accounting/reports/trial-balance/', { params: params! })
      return res.data
    },
    enabled: !!(params?.date_from && params?.date_to),
  })
}

export function useEightColumnBalance(
  params: { date_from: string; date_to: string } | null
) {
  return useQuery({
    queryKey: accountingKeys.eightColumnBalance(params ?? undefined),
    queryFn: async () => {
      const res = await apiClient.get<EightColumnBalanceResponse>(
        '/accounting/reports/eight-column-balance/', { params: params! }
      )
      return res.data
    },
    enabled: !!(params?.date_from && params?.date_to),
  })
}

export function useClassifiedBalance(
  params: { date_to: string } | null
) {
  return useQuery({
    queryKey: accountingKeys.classifiedBalance(params ?? undefined),
    queryFn: async () => {
      const res = await apiClient.get<ClassifiedBalanceResponse>(
        '/accounting/reports/classified-balance/', { params: params! }
      )
      return res.data
    },
    enabled: !!params?.date_to,
  })
}

export function useIncomeStatement(
  // `cost_center_id` incluye a los centros hijos (lo resuelve el backend).
  params: { date_from: string; date_to: string; cost_center_id?: string } | null
) {
  return useQuery({
    queryKey: accountingKeys.incomeStatement(params ?? undefined),
    queryFn: async () => {
      const res = await apiClient.get<IncomeStatementResponse>(
        '/accounting/reports/income-statement/', { params: params! }
      )
      return res.data
    },
    enabled: !!(params?.date_from && params?.date_to),
  })
}

export function useCashFlowStatement(
  params: { date_from: string; date_to: string } | null
) {
  return useQuery({
    queryKey: accountingKeys.cashFlow(params ?? undefined),
    queryFn: async () => {
      const res = await apiClient.get<CashFlowStatementResponse>(
        '/accounting/reports/cash-flow/', { params: params! }
      )
      return res.data
    },
    enabled: !!(params?.date_from && params?.date_to),
  })
}

// ─── CON-05: Análisis de Cuentas ──────────────────────────────────────────────

export function useAccountAnalysis(params: {
  account_id: string
  date_from: string
  date_to: string
  cost_center_id?: string
} | null) {
  return useQuery({
    queryKey: accountingKeys.analysis(params ?? undefined),
    queryFn: async () => {
      const res = await apiClient.get<AccountAnalysisResponse>('/accounting/analysis/', { params: params! })
      return res.data
    },
    enabled: !!(params?.account_id && params?.date_from && params?.date_to),
  })
}

// ─── CON-07: Rechazar Comprobante ─────────────────────────────────────────────

export function useRejectVoucher() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiClient.post<AccountingVoucher>(`/accounting/vouchers/${id}/reject/`, { reason }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] }),
  })
}

// ─── CON-06: Conciliación Bancaria ────────────────────────────────────────────

export function useBankReconciliations(params?: { account_id?: string; status?: string }) {
  return useQuery({
    queryKey: accountingKeys.reconciliations(params),
    queryFn: async () => {
      const res = await apiClient.get<{ count: number; reconciliations: BankReconciliation[] }>(
        '/accounting/bank-reconciliation/', { params }
      )
      return res.data
    },
  })
}

export function useBankReconciliation(id: string) {
  return useQuery({
    queryKey: accountingKeys.reconciliation(id),
    queryFn: async () => {
      const res = await apiClient.get<BankReconciliationDetail>(
        `/accounting/bank-reconciliation/${id}/`
      )
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateReconciliation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { bank_account: string; period_start: string; period_end: string }) =>
      apiClient.post<BankReconciliation>('/accounting/bank-reconciliation/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'bank-reconciliation'] }),
  })
}

export function useUploadBankStatement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file, balanceOpening, balanceClosing, mapping, separator, dateFormat, skipRows }: {
      id: string
      file: File
      balanceOpening: string
      balanceClosing: string
      mapping?: Record<string, string>
      separator?: string
      dateFormat?: string
      skipRows?: number
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('balance_opening', balanceOpening)
      formData.append('balance_closing', balanceClosing)
      if (mapping) formData.append('mapping', JSON.stringify(mapping))
      if (separator) formData.append('separator', separator)
      if (dateFormat) formData.append('date_format', dateFormat)
      if (skipRows && skipRows > 0) formData.append('skip_rows', String(skipRows))
      return apiClient.post(
        `/accounting/bank-reconciliation/${id}/upload-statement/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ).then(r => r.data)
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: accountingKeys.reconciliation(id) })
    },
  })
}

export function useAutoReconcile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/accounting/bank-reconciliation/${id}/auto-reconcile/`).then(r => r.data),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: accountingKeys.reconciliation(id) })
    },
  })
}

export function useManualMatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, statementLineId, voucherLineId }: {
      id: string
      statementLineId: string
      voucherLineId: string
    }) =>
      apiClient.post(`/accounting/bank-reconciliation/${id}/match/`, {
        statement_line_id: statementLineId,
        voucher_line_id: voucherLineId,
      }).then(r => r.data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: accountingKeys.reconciliation(id) })
    },
  })
}

export function useCloseReconciliation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<BankReconciliation>(
        `/accounting/bank-reconciliation/${id}/close/`
      ).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting', 'bank-reconciliation'] })
    },
  })
}

// ─── AccountingDefaults ────────────────────────────────────────────────────────

export interface AccountingDefaults {
  id: number
  account_receivable: string | null
  account_receivable_code?: string | null
  account_receivable_name?: string | null
  account_sales_revenue: string | null
  account_sales_revenue_code?: string | null
  account_sales_revenue_name?: string | null
  account_vat_payable: string | null
  account_vat_payable_code?: string | null
  account_vat_payable_name?: string | null
  account_cash_bank: string | null
  account_cash_bank_code?: string | null
  account_cash_bank_name?: string | null
  account_factoring_commission: string | null
  account_factoring_commission_code?: string | null
  account_factoring_commission_name?: string | null
  account_payable: string | null
  account_payable_code?: string | null
  account_payable_name?: string | null
  account_purchase_exp: string | null
  account_purchase_exp_code?: string | null
  account_purchase_exp_name?: string | null
  account_vat_credit: string | null
  account_vat_credit_code?: string | null
  account_vat_credit_name?: string | null
  account_vat_withheld: string | null
  account_vat_withheld_code?: string | null
  account_vat_withheld_name?: string | null
  account_non_billable_charges: string | null
  account_non_billable_charges_code?: string | null
  account_non_billable_charges_name?: string | null
  account_exchange_difference: string | null
  account_exchange_difference_code?: string | null
  account_exchange_difference_name?: string | null
  // Cuentas de nómina (provisión y pago de remuneraciones)
  account_salaries_expense: string | null
  account_employer_contributions_expense: string | null
  account_salaries_payable: string | null
  account_afp_payable: string | null
  account_health_payable: string | null
  account_health_additional_payable: string | null
  account_tax_2cat_payable: string | null
  account_unemployment_payable: string | null
  account_mutual_payable: string | null
  account_sis_payable: string | null
  account_apv_payable: string | null
  account_business_salary_expense: string | null
  account_pension_reform_payable: string | null
  account_other_deductions_payable: string | null
  account_voluntary_savings_payable: string | null
  account_family_allowance_receivable: string | null
  // Auto-aprobación de comprobantes automáticos por entidad
  auto_approve_sales: boolean
  auto_approve_collections: boolean
  auto_approve_purchases: boolean
  auto_approve_payments: boolean
  auto_approve_payroll: boolean
  auto_approve_honorarios: boolean
}

export function useAccountingDefaults() {
  return useQuery({
    queryKey: ['accounting', 'defaults'],
    queryFn: async () => {
      const res = await apiClient.get<AccountingDefaults>('/accounting/defaults/')
      return res.data
    },
  })
}

export function useUpdateAccountingDefaults() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<AccountingDefaults>) =>
      apiClient.patch<AccountingDefaults>('/accounting/defaults/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'defaults'] }),
  })
}

// ─── Tipos de descuento de nómina (catálogo → cuenta contable) ────────────────

export interface PayrollDeductionType {
  id: string
  code: string
  name: string
  account: string | null
  account_code?: string | null
  account_name?: string | null
  nature: 'liability' | 'asset'
  nature_display?: string
  /** Campo del archivo Previred al que se declara este descuento. Vacío = no se
   *  declara a la CCAF. */
  previred_field?: PreviredCcafField | ''
  previred_field_display?: string
  is_active: boolean
}

/** Los seis campos de descuento de la CCAF en el layout de 105 campos.
 *
 *  🔴 La caja recauda estos montos junto con la cotización de salud, así que lo
 *  que la empresa retiene por planilla tiene que viajar declarado o la CCAF no lo
 *  acredita: el trabajador sigue apareciendo como deudor. Previred **no** lo
 *  detecta —no sabe cuánto retuvo la empresa— así que el archivo pasa su
 *  validador igual. */
export type PreviredCcafField =
  | 'cred_pers_ccaf'
  | 'desc_dental_ccaf'
  | 'desc_leasing'
  | 'desc_seg_vida_ccaf'
  | 'otros_desc_ccaf'
  | 'desc_cargas_ccaf'

export const PREVIRED_CCAF_FIELDS: { value: PreviredCcafField; label: string }[] = [
  { value: 'cred_pers_ccaf', label: 'Crédito social (campo 85)' },
  { value: 'desc_dental_ccaf', label: 'Dental (campo 86)' },
  { value: 'desc_leasing', label: 'Leasing (campo 87)' },
  { value: 'desc_seg_vida_ccaf', label: 'Seguro de vida (campo 88)' },
  { value: 'otros_desc_ccaf', label: 'Otros descuentos (campo 89)' },
  { value: 'desc_cargas_ccaf', label: 'Descuento de cargas (campo 91)' },
]

const DEDUCTION_TYPES_KEY = ['payroll', 'deduction-types'] as const

export function usePayrollDeductionTypes() {
  return useQuery({
    queryKey: DEDUCTION_TYPES_KEY,
    queryFn: async () => {
      const res = await apiClient.get<{ deduction_types: PayrollDeductionType[]; count: number }>(
        '/payroll/deduction-types/',
      )
      return res.data.deduction_types
    },
  })
}

export function useSavePayrollDeductionType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<PayrollDeductionType> & { id?: string }) => {
      const { id, ...body } = data
      return id
        ? apiClient.patch<PayrollDeductionType>(`/payroll/deduction-types/${id}/`, body).then(r => r.data)
        : apiClient.post<PayrollDeductionType>('/payroll/deduction-types/', body).then(r => r.data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DEDUCTION_TYPES_KEY }),
  })
}

export function useDeletePayrollDeductionType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/payroll/deduction-types/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: DEDUCTION_TYPES_KEY }),
  })
}

// ─── Cruce Contable (Audit) ──────────────────────────────────────────────────

export function useAccountingAudit(params?: { date_from?: string; date_to?: string }) {
  return useQuery({
    queryKey: accountingKeys.audit(params),
    queryFn: async () => {
      const res = await apiClient.get<AccountingAuditResponse>('/accounting/audit/', { params })
      return res.data
    },
    staleTime: 2 * 60 * 1000,
  })
}

// ─── Contabilizar — Acción manual ────────────────────────────────────────────

export interface ContabilizarRequest {
  source_type: string
  source_id: string
}

export interface ContabilizarResponse {
  success: boolean
  voucher_id: string | null
  error: string | null
}

export interface ContabilizarBatchResponse {
  success: number
  errors: number
  details: { source_type: string; source_id: string; error: string }[]
}

export function useContabilizar() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ContabilizarRequest) =>
      apiClient.post<ContabilizarResponse>('/accounting/contabilizar/', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting', 'audit'] })
      qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] })
      qc.invalidateQueries({ queryKey: ['sales'] })
      qc.invalidateQueries({ queryKey: ['purchases'] })
    },
  })
}

// ─── Plan de Cuentas SII (schema public) ────────────────────────────────────

export function useSIIAccounts(params?: { search?: string; is_active?: string }) {
  return useQuery({
    queryKey: ['sii-accounts', params],
    queryFn: async () => {
      const res = await apiClient.get<SIIAccountListResponse>('/platform/sii-accounts/', { params })
      return res.data
    },
    staleTime: 10 * 60 * 1000, // 10 min — rarely changes
  })
}

export function useCreateSIIAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<SIIAccount>) =>
      apiClient.post<SIIAccount>('/platform/sii-accounts/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sii-accounts'] }),
  })
}

export function useUpdateSIIAccount(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<SIIAccount>) =>
      apiClient.patch<SIIAccount>(`/platform/sii-accounts/${id}/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sii-accounts'] }),
  })
}

export function useDeleteSIIAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiClient.delete(`/platform/sii-accounts/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sii-accounts'] }),
  })
}

export function useContabilizarBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (items?: ContabilizarRequest[]) =>
      apiClient.post<ContabilizarBatchResponse>(
        '/accounting/contabilizar-batch/',
        items ? { items } : {},
      ).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting', 'audit'] })
      qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] })
      qc.invalidateQueries({ queryKey: ['sales'] })
      qc.invalidateQueries({ queryKey: ['purchases'] })
    },
  })
}

// ─── Adjuntos de Comprobantes ────────────────────────────────────────────────

export interface VoucherAttachment {
  id: string
  voucher: string
  file_name: string
  file_size: number
  content_type: string
  download_url: string
  uploaded_by: string | null
  uploaded_by_name: string | null
  created_at: string
}

export interface VoucherAttachmentListResponse {
  count: number
  total_size: number
  max_size: number
  attachments: VoucherAttachment[]
}

export function useVoucherAttachments(voucherId: string) {
  return useQuery({
    queryKey: accountingKeys.attachments(voucherId),
    queryFn: async () => {
      const res = await apiClient.get<VoucherAttachmentListResponse>(
        `/accounting/vouchers/${voucherId}/attachments/`
      )
      return res.data
    },
    enabled: !!voucherId,
  })
}

export function useUploadVoucherAttachment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ voucherId, file }: { voucherId: string; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      return apiClient.post<VoucherAttachment>(
        `/accounting/vouchers/${voucherId}/attachments/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ).then(r => r.data)
    },
    onSuccess: (_data, { voucherId }) => {
      qc.invalidateQueries({ queryKey: accountingKeys.attachments(voucherId) })
    },
  })
}

export function useDeleteVoucherAttachment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ voucherId, attachmentId }: { voucherId: string; attachmentId: string }) =>
      apiClient.delete(`/accounting/vouchers/${voucherId}/attachments/${attachmentId}/`),
    onSuccess: (_data, { voucherId }) => {
      qc.invalidateQueries({ queryKey: accountingKeys.attachments(voucherId) })
    },
  })
}

// ── Notas de Gasto ──────────────────────────────────────────────────────────
export interface ExpenseNote {
  id: string; date: string; description: string; amount: string;
  account_code: string; account_name: string; cost_center_name: string | null;
  payment_method: string; reference: string; status: string;
  created_by_name: string; approved_by_name?: string | null;
  approved_at?: string | null; voucher?: string | null;
  created_at: string;
}
export interface ExpenseListResponse { expenses: ExpenseNote[]; count: number; page: number; page_size: number; pages: number }

export function useExpenses(params?: { status?: string; search?: string; page?: number; page_size?: number }) {
  return useQuery<ExpenseListResponse>({ queryKey: ['accounting', 'expenses', params], queryFn: async () => { const r = await apiClient.get('/accounting/expenses/', { params }); return r.data } })
}
export function useCreateExpense() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (data: Record<string, unknown>) => { const r = await apiClient.post('/accounting/expenses/', data); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'expenses'] }) })
}
export function useApproveExpense() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { const r = await apiClient.post(`/accounting/expenses/${id}/approve/`); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'expenses'] }) })
}
export function useRejectExpense() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { const r = await apiClient.post(`/accounting/expenses/${id}/reject/`); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'expenses'] }) })
}

// ── Presupuestos ─────────────────────────────────────────────────────────────
export interface BudgetItem {
  id: string; name: string; year: number; month: number | null; period_type: string;
  account: string; account_code: string; account_name: string;
  cost_center: string | null; cost_center_name: string | null;
  budgeted_amount: string; notes: string;
}
export interface BudgetListResponse { budgets: BudgetItem[]; count: number }
export interface BudgetVarianceItem {
  budget_id: string; name: string; account_code: string; account_name: string;
  cost_center_name: string | null; period: string;
  budgeted: string; actual: string; variance: string; variance_pct: string;
  status: 'over' | 'under';
}
export interface BudgetVarianceResponse { variance: BudgetVarianceItem[]; count: number }

export function useBudgets(params?: { year?: number; month?: number; cost_center_id?: string }) {
  return useQuery<BudgetListResponse>({ queryKey: ['accounting', 'budgets', params], queryFn: async () => { const r = await apiClient.get('/accounting/budgets/', { params }); return r.data } })
}
export function useCreateBudget() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (data: Record<string, unknown>) => { const r = await apiClient.post('/accounting/budgets/', data); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'budgets'] }) })
}
export function useDeleteBudget() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { await apiClient.delete(`/accounting/budgets/${id}/`); }, onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'budgets'] }) })
}
export function useBudgetVariance(params?: { year?: number; month?: number; cost_center_id?: string }) {
  return useQuery<BudgetVarianceResponse>({ queryKey: ['accounting', 'budgets', 'variance', params], queryFn: async () => { const r = await apiClient.get('/accounting/budgets/variance/', { params }); return r.data } })
}

// ── Activos Fijos ────────────────────────────────────────────────────────────
export interface FixedAssetItem {
  id: string; code: string; name: string; category: string; category_display: string;
  acquisition_date: string; acquisition_cost: string;
  depreciation_method: string; monthly_depreciation: string;
  total_depreciated: string; book_value: string;
  status: string; status_display: string;
  cost_center_name: string | null; location: string;
}
export interface FixedAssetListResponse { assets: FixedAssetItem[]; count: number }

export function useFixedAssets(params?: { status?: string; search?: string }) {
  return useQuery<FixedAssetListResponse>({ queryKey: ['accounting', 'fixed-assets', params], queryFn: async () => { const r = await apiClient.get('/accounting/fixed-assets/', { params }); return r.data } })
}
export function useCreateFixedAsset() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (data: Record<string, unknown>) => { const r = await apiClient.post('/accounting/fixed-assets/', data); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'fixed-assets'] }) })
}
export function useDisposeFixedAsset() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async ({ id, ...data }: { id: string; disposed_date?: string; reason?: string }) => { const r = await apiClient.post(`/accounting/fixed-assets/${id}/dispose/`, data); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'fixed-assets'] }) })
}


// ── Tasa de cambio on-demand ─────────────────────────────────────────────────
export interface CurrencyRateForDate {
  currency: string
  rate: string
  rate_date: string
  source: string
}

export function useCurrencyRateForDate(currency: string, date: string) {
  return useQuery({
    queryKey: ['accounting', 'currency-rate-for-date', currency, date],
    queryFn: async () => {
      const res = await apiClient.get('/config/currency-rates/for-date/', {
        params: { currency, date },
      })
      return res.data as CurrencyRateForDate
    },
    enabled: !!currency && currency !== 'CLP' && !!date,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  })
}

// ─── PeriodLock — bloqueo manual de períodos contables ───────────────────────

export interface PeriodLock {
  id: string
  start_date: string
  end_date: string
  reason: string
  is_active: boolean
  locked_by: string | null
  locked_by_email: string | null
  locked_at: string
  unlocked_by: string | null
  unlocked_by_email: string | null
  unlocked_at: string | null
  unlock_reason: string
  notes: string
  created_at: string
  updated_at: string
}

export interface PeriodLockListResponse {
  count: number
  locks: PeriodLock[]
}

export function usePeriodLocks(params?: { active?: boolean; contains_date?: string }) {
  return useQuery({
    queryKey: ['accounting', 'period-locks', params],
    queryFn: async () => {
      const res = await apiClient.get<PeriodLockListResponse>('/accounting/period-locks/', {
        params: {
          active: params?.active === undefined ? undefined : String(params.active),
          contains_date: params?.contains_date,
        },
      })
      return res.data
    },
  })
}

export function useCreatePeriodLock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { start_date: string; end_date: string; reason: string; notes?: string }) => {
      const res = await apiClient.post<PeriodLock>('/accounting/period-locks/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'period-locks'] }),
  })
}

export function useUnlockPeriod(lockId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { reason: string }) => {
      const res = await apiClient.post<PeriodLock>(`/accounting/period-locks/${lockId}/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'period-locks'] }),
  })
}

// ─── FiscalYear — cierre de ejercicio ───────────────────────────────────────

export interface FiscalYear {
  id: string
  year: number
  status: 'open' | 'closing' | 'closed'
  result_account: string | null
  result_account_code: string | null
  result_account_name: string | null
  pyl_voucher: string | null
  equity_voucher: string | null
  opening_voucher: string | null
  period_lock: string | null
  closed_at: string | null
  closed_by: string | null
  closed_by_email: string | null
  notes: string
  created_at: string
  updated_at: string
}

export interface FiscalYearListResponse {
  count: number
  fiscal_years: FiscalYear[]
}

export interface FiscalYearPreview {
  year: number
  pyl_lines: Array<{
    code: string; name: string; account_type: string;
    debe: string; haber: string; balance: string; closing_side: 'debe' | 'haber'
  }>
  pyl_total_revenue: string
  pyl_total_expense: string
  pyl_result: string
  opening_lines: Array<{
    code: string; name: string; account_type: string;
    debe: string; haber: string; opening_debe: string; opening_haber: string
  }>
  opening_total_debe: string
  opening_total_haber: string
}

export function useFiscalYears() {
  return useQuery({
    queryKey: ['accounting', 'fiscal-years'],
    queryFn: async () => {
      const res = await apiClient.get<FiscalYearListResponse>('/accounting/fiscal-years/')
      return res.data
    },
  })
}

export function useCreateFiscalYear() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { year: number; notes?: string }) => {
      const res = await apiClient.post<FiscalYear>('/accounting/fiscal-years/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'fiscal-years'] }),
  })
}

export function useFiscalYearPreviewClose(fyId: string, enabled = true) {
  return useQuery({
    queryKey: ['accounting', 'fiscal-years', fyId, 'preview-close'],
    queryFn: async () => {
      const res = await apiClient.get<FiscalYearPreview>(`/accounting/fiscal-years/${fyId}/preview-close/`)
      return res.data
    },
    enabled: enabled && !!fyId,
  })
}

export function useCloseFiscalYear(fyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { result_account: string }) => {
      const res = await apiClient.post<FiscalYear>(`/accounting/fiscal-years/${fyId}/close/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting', 'fiscal-years'] })
      qc.invalidateQueries({ queryKey: ['accounting', 'period-locks'] })
    },
  })
}

export interface BalanceDiffEntry {
  account_id: string
  code: string
  name: string
  account_type: string
  calculated_debe: string
  calculated_haber: string
  calculated_net: string
  expected_debe: string
  expected_haber: string
  expected_net: string
  delta_net: string
  adjust_debe: string
  adjust_haber: string
}

export interface ExpectedBalanceInput {
  account_id?: string
  code?: string
  expected_debe: string
  expected_haber: string
}

export function useFiscalYearBalanceDiff(fyId: string) {
  return useMutation({
    mutationFn: async (data: { expected_balances: ExpectedBalanceInput[] }) => {
      const res = await apiClient.post<{ year: number; count: number; diff: BalanceDiffEntry[] }>(
        `/accounting/fiscal-years/${fyId}/balance-diff/`, data
      )
      return res.data
    },
  })
}

export function useFiscalYearGenerateAdjustment(fyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      expected_balances: ExpectedBalanceInput[]
      contraparte_account: string
      glosa?: string
    }) => {
      const res = await apiClient.post(`/accounting/fiscal-years/${fyId}/generate-adjustment/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] }),
  })
}

export function useFiscalYearParseBalanceExcel(fyId: string) {
  return useMutation({
    mutationFn: async (data: { file: File; sheet?: string }) => {
      const fd = new FormData()
      fd.append('file', data.file)
      if (data.sheet) fd.append('sheet', data.sheet)
      const res = await apiClient.post(`/accounting/fiscal-years/${fyId}/parse-balance-excel/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data as {
        sheet_used: string
        sheets_available: string[]
        rows: Array<{ code: string; name: string; expected_debe: string; expected_haber: string }>
        error?: string
      }
    },
  })
}
